import { ref, readonly } from "vue";
import { rpc } from "./supabase";

/**
 * Pele (skin) da plataforma.
 *
 * É um eixo INDEPENDENTE do claro/escuro: cada pele define os seus tokens nos
 * dois modos (ver src/styles/app.css). "classico" é a ausência do atributo
 * data-theme; qualquer outra pele liga um bloco [data-theme="..."] no <html>.
 *
 * A pele vigente é GLOBAL da plataforma (guardada em app_config.key='tema', que
 * tem segredos e RLS: por isso o cliente NUNCA lê a tabela direto; usa as RPCs
 * fn_tema_atual (todos, inclui campo/anon) e fn_tema_definir (só staff/admin)).
 * Só o admin troca, em Configurações. O showroom usa um "preview" de sessão
 * (sessionStorage) que sobrepõe a global apenas naquele navegador, sem mexer no
 * que os outros veem.
 */
export type Skin = "classico" | "novo";
export const SKINS: { value: Skin; label: string; descricao: string }[] = [
  { value: "classico", label: "Clássico", descricao: "Navy indigo e cereja. O tema atual da plataforma." },
  { value: "novo", label: "Novo", descricao: "Marinho, azul médio e vermelhão. Design system 2026." },
];

const KEY_PREVIEW = "aferi:ui:skinPreview"; // sessão, só este navegador
const KEY_CACHE = "aferi:ui:skin";          // último valor global conhecido (evita flash)

let globalSkin: Skin = "classico";

// Pele efetiva como ref reativa: componentes que mudam de ESTRUTURA por tema
// (ex.: chip de ícone no KPI) leem isto. Todas as trocas passam por applySkin.
const _skin = ref<Skin>("classico");
/** Ref (só leitura) com a pele aplicada agora. Reativo à troca de tema. */
export function useSkin() {
  return readonly(_skin);
}

function ehSkin(v: unknown): v is Skin {
  return v === "classico" || v === "novo";
}

/** Liga/desliga o atributo data-theme no <html>. */
export function applySkin(skin: Skin) {
  const root = document.documentElement;
  if (skin === "classico") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", skin);
  _skin.value = skin;
}

export function getPreview(): Skin | null {
  try {
    const v = sessionStorage.getItem(KEY_PREVIEW);
    return ehSkin(v) ? v : null;
  } catch {
    return null;
  }
}

/** Preview de sessão (showroom). null volta para a pele global. */
export function setPreview(skin: Skin | null) {
  try {
    if (!skin) sessionStorage.removeItem(KEY_PREVIEW);
    else sessionStorage.setItem(KEY_PREVIEW, skin);
  } catch {}
  applySkin(skin ?? globalSkin);
}

/** Pele efetivamente aplicada agora (preview vence global). */
export function skinEfetiva(): Skin {
  return getPreview() ?? globalSkin;
}

export function skinGlobal(): Skin {
  return globalSkin;
}

/**
 * Aplica de imediato, SEM rede, a última pele conhecida (ou o preview),
 * para o app não abrir com uma cor e trocar depois. Chamar cedo no main.ts.
 */
export function applyCachedSkinSync() {
  let cache: Skin = "classico";
  try {
    const v = localStorage.getItem(KEY_CACHE);
    if (ehSkin(v)) cache = v;
  } catch {}
  globalSkin = cache;
  applySkin(getPreview() ?? cache);
}

/**
 * Resolve a pele global pela RPC fn_tema_atual (só a chave 'tema', legível por
 * qualquer cliente). NÃO pode segurar o mount (mesma regra do branding): roda em
 * paralelo e só reaplica quando a resposta chega.
 */
export async function bootSkin(): Promise<Skin> {
  try {
    const v = await rpc<string>("fn_tema_atual", {});
    globalSkin = ehSkin(v) ? v : "classico";
  } catch {
    // sem rede: mantém o cache aplicado por applyCachedSkinSync
  }
  try { localStorage.setItem(KEY_CACHE, globalSkin); } catch {}
  applySkin(getPreview() ?? globalSkin);
  return globalSkin;
}

/**
 * Grava a pele global via RPC fn_tema_definir (valida o valor e exige staff/admin
 * no banco) e limpa o preview local, passando a valer para todos.
 */
export async function salvarSkinGlobal(skin: Skin) {
  await rpc("fn_tema_definir", { p_skin: skin });
  globalSkin = skin;
  try { localStorage.setItem(KEY_CACHE, skin); } catch {}
  setPreview(null); // aplica a global e descarta o preview de sessão
}
